import {put, call} from 'redux-saga/effects';
import {normalize} from 'normalizr';
import * as schema from '_redux/schema';
import requestHandler from '_services/api/axios-config';
import {SagaIterator} from 'redux-saga';
import {HttpMethods} from '_services/api/axios.types';
import history from '_helpers/history';
import {toast} from 'react-toastify';
import log from 'loglevel';
import {UserRoles} from '_redux/user/auth/user-auth.types';
import {getUpdatedUserStart} from '_redux/user/auth/user-auth.actions';
import {removePatientApptsBeforeNow} from '_helpers/utils';
import {
  FetchPatientAppointmentsStartAction,
  FetchAvailabilityInfoStartAction,
  FetchProviderAppointmentsStartAction,
  BookAppointmentAction,
  CancelPatientAppointmentStartAction,
  FetchRecurringAvailabilityStartAction,
  BookRecurringAppointmentAction,
} from './appointments-actions.types';
import {
  fetchPatientAppointmentsSuccess,
  fetchPatientAppointmentsFailure,
  fetchProviderAppointmentsSuccess,
  fetchProviderAppointmentsFailure,
  fetchAvailabilityInfoSuccess,
  fetchAvailabilityInfoFailure,
  cancelPatientAppointmentSuccess,
  fetchRecurringAvailabilitySuccess,
  fetchRecurringAvailabilityFailure,
  bookRecurringAppointmentSuccess,
  bookRecurringAppointmentFailure,
} from './appointments.actions';
import {AppointmentTypes} from './appointments.types';

export function* fetchPatientAppointmentsStartSaga({
  payload: {numRequest},
}: FetchPatientAppointmentsStartAction): SagaIterator<void> {
  try {
    const {
      data: {
        message: {appointments},
      },
    } = yield call(requestHandler, {
      method: HttpMethods.GET,
      url: `/api/patient/acuity/appointments/?numRequest=${numRequest}`,
    });
    const filteredAppointments = yield call(
      removePatientApptsBeforeNow,
      appointments,
    );
    yield put(fetchPatientAppointmentsSuccess(filteredAppointments));
  } catch (error) {
    yield put(fetchPatientAppointmentsFailure());
  }
}

export function* fetchProviderAppointmentsStartSaga({
  payload: {userRole},
}: FetchProviderAppointmentsStartAction): SagaIterator<void> {
  try {
    const {
      data: {
        message: {appointments},
      },
    } = yield call(requestHandler, {
      method: HttpMethods.GET,
      url: `/api/${userRole}/acuity/appointments/?numRequest=0`,
    });
    yield put(fetchProviderAppointmentsSuccess(appointments));
  } catch (error) {
    yield put(fetchProviderAppointmentsFailure());
  }
}

export function* fetchAvailabilityInfoStartSaga({
  payload: {appointmentType, yearAndMonth, email, callback},
}: FetchAvailabilityInfoStartAction): SagaIterator<void> {
  try {
    const {
      data: {
        message: {appointmentTypeID, calendarAvailability},
      },
    } = yield call(requestHandler, {
      method: HttpMethods.GET,
      url: `/api/patient/acuity/available-time/?typeofAppointment=${appointmentType}&availableMonth=${yearAndMonth}&email=${email}`,
    });
    const normalizedAvailabilityInfo = yield call(
      normalize,
      calendarAvailability,
      schema.arrayOfAvailabilityInfo,
    );
    yield put(
      fetchAvailabilityInfoSuccess({
        ...normalizedAvailabilityInfo,
        appointmentType,
        appointmentTypeId: appointmentTypeID,
      }),
    );
    yield call(callback);
  } catch (error) {
    yield put(fetchAvailabilityInfoFailure());
  }
}

export function* bookAppointmentSaga({
  payload: {
    appointmentDateTimeString,
    providerId,
    appointmentType,
    calendarId,
    patientTimezone,
    appointmentTypeID,
    patientEmail,
    setSubmitting,
  },
}: BookAppointmentAction): SagaIterator<void> {
  try {
    yield call(requestHandler, {
      method: HttpMethods.POST,
      url: '/api/patient/acuity/book-appointment',
      data: {
        appointmentDateTimeString,
        [`${
          appointmentType === AppointmentTypes.video_call_with_therapist
            ? 'therapistId'
            : 'prescriberId'
        }`]: providerId,
        patientEmail,
        calendarId,
        appointmentTypeID,
        patientTimezone,
      },
    });
    if (!patientEmail) {
      yield put(getUpdatedUserStart(UserRoles.member));
      history.push('/dashboard');
    }
    toast.success('Appointment booked successfully');
    yield call(setSubmitting, false);
  } catch (error) {
    yield call(setSubmitting, false);
  }
}

export function* cancelPatientAppointmentStartSaga({
  payload: appointmentID,
}: CancelPatientAppointmentStartAction): SagaIterator<void> {
  try {
    yield call(requestHandler, {
      method: HttpMethods.POST,
      url: '/api/patient/acuity/cancel-appointment',
      data: {appointmentID},
    });
    yield put(cancelPatientAppointmentSuccess(appointmentID));
    toast.success('Appointment cancelled successfully');
  } catch (error) {
    log.warn(error);
  }
}

export function* fetchRecurringAvailabilityStartSaga({
  payload: {
    duration,
    numberOfWeeks,
    patientEmail,
    providerId,
    startDate,
    appointmentType,
    state,
    eventTypeTitle,
    callback,
  },
}: FetchRecurringAvailabilityStartAction): SagaIterator<void> {
  try {
    const queryParams = new URLSearchParams({
      startDate,
      duration,
      numberOfWeeks,
      ...(patientEmail && {patientEmail}),
      ...(providerId && {providerId}),
      ...(appointmentType && {appointmentType}),
      ...(state && {state}),
      ...(eventTypeTitle && {eventTypeTitle}),
    });

    const {
      data: {message},
    } = yield call(requestHandler, {
      method: HttpMethods.GET,
      url: `/api/admin/calendar/session/recurring-times?${queryParams.toString()}`,
    });

    const availability = Array.isArray(message)
      ? {slots: message, availabilityByDate: []}
      : message;

    yield put(fetchRecurringAvailabilitySuccess(availability));
    if (callback) yield call(() => callback(availability));
  } catch (error) {
    log.error('Error fetching recurring availability:', error);
    const errorMessage =
      error instanceof Error ? error.message : 'Failed to fetch availability';
    yield put(fetchRecurringAvailabilityFailure(errorMessage));
  }
}

export function* bookRecurringAppointmentSaga({
  payload: {
    numberOfWeeks,
    appointmentStartDateTimeString,
    therapistId,
    prescriberId,
    patientTimezone,
    appointmentDuration,
    appointmentTypeId,
    patientEmail,
    setSubmitting,
    patientFullName,
    patientPhoneNumber,
  },
}: BookRecurringAppointmentAction): SagaIterator<void> {
  try {
    const requestData = {
      numberOfWeeks,
      appointmentStartDateTimeString,
      ...(therapistId && {providerId: therapistId}),
      ...(prescriberId && {providerId: prescriberId}),
      patientTimezone,
      appointmentDuration,
      appointmentTypeId,
      ...(patientEmail && {email: patientEmail}),
      ...(patientFullName && {
        firstName: patientFullName.split(' ')[0],
        lastName: patientFullName.split(' ')[1],
      }),
      ...(patientPhoneNumber && {phoneNumber: patientPhoneNumber}),
    };

    const response = yield call(requestHandler, {
      method: HttpMethods.POST,
      url: '/api/admin/calendar/session/recurring/schedule',
      data: requestData,
    });

    yield put(bookRecurringAppointmentSuccess());
    if (response?.data?.message?.bookingStatus?.hasFailures) {
      const {
        successfullyBooked,
        totalRequested,
        failed,
      } = response.data.message.bookingStatus;
      toast.warn(
        `Partial success: ${successfullyBooked}/${totalRequested} appointments booked. ${failed} appointments failed. Check email for details.`,
        {autoClose: 8000},
      );
    } else {
      const totalBooked =
        response?.data?.message?.bookingStatus?.successfullyBooked ||
        numberOfWeeks;
      toast.success(
        `All ${totalBooked} recurring appointments booked successfully!`,
      );
    }
    yield call(setSubmitting, false);
  } catch (error) {
    log.error('Error booking recurring appointment:', error);
    yield put(bookRecurringAppointmentFailure());
    yield call(setSubmitting, false);
  }
}
